import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/lib/api";
import { LanguageTabs } from "./LanguageTabs";
import { AnnouncementPicker } from "./AnnouncementPicker";
import { UnavailableToggle } from "./UnavailableToggle";
import {
  VariantFields,
  emptyVariant,
  variantDraftToPayload,
  validateVariantDraft,
  type VariantDraft,
  type VariantPayload,
} from "./VariantFields";

export type ProductFormValues = {
  name: string;
  name_en: string;
  description: string;
  description_en: string;
  category_id: number;
  brand_id: number;
};

export type VariantSubmission = {
  payload: VariantPayload;
  imageFile: File | null;
  announcement_id: string | null;
  is_unavailable: boolean;
};

export type ProductFormSubmitValues = ProductFormValues & {
  variants?: VariantSubmission[];
  productId?: string;
};

type Category = { id: number; name: string; name_en: string };
type Brand = { id: number; name: string };
type Page<T> = { items: T[] };

type ProductFormDefaults = Partial<ProductFormValues> & {
  id?: string;
  category?: { name: string };
  brand?: { name: string };
};

type ProductFormProps = {
  defaultValues?: ProductFormDefaults;
  submitLabel: string;
  pendingLabel?: string;
  onSubmit: (data: ProductFormSubmitValues) => Promise<void>;
  mode?: "create" | "edit";
  hideSubmitButton?: boolean;
  onFieldsReadyChange?: (ready: boolean) => void;
  onExposeSubmit?: (trigger: () => Promise<void>) => void;
};

// Only the Spanish name/description are required - the English fields are
// optional translations, never a blocker for saving or publishing.
const REQUIRED_FIELDS_READY = (values: Partial<ProductFormValues>): boolean =>
  Boolean(
    values.name?.trim() &&
      values.description?.trim() &&
      values.category_id &&
      !Number.isNaN(values.category_id) &&
      values.brand_id &&
      !Number.isNaN(values.brand_id)
  );

export function ProductForm({
  defaultValues,
  submitLabel,
  pendingLabel,
  onSubmit,
  mode = "edit",
  hideSubmitButton = false,
  onFieldsReadyChange,
  onExposeSubmit,
}: ProductFormProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<ProductFormValues>({ defaultValues });

  const [activeLanguage, setActiveLanguage] = useState<"es" | "en">("es");
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [variantDrafts, setVariantDrafts] = useState<VariantDraft[]>([{ ...emptyVariant }]);
  const [variantsError, setVariantsError] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [draftAnnouncementIds, setDraftAnnouncementIds] = useState<(string | null)[]>([null]);
  const [draftUnavailable, setDraftUnavailable] = useState<boolean[]>([false]);
  const [variantActionNudge, setVariantActionNudge] = useState(0);

  const draftIdRef = useRef<string | null>(defaultValues?.id ?? null);
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSaveQueued = <T,>(task: () => Promise<T>): Promise<T> => {
    const result = saveChainRef.current.catch(() => {}).then(task);
    saveChainRef.current = result.catch(() => {});
    return result;
  };

  const persistDraft = (values: Partial<ProductFormValues>): Promise<string | null> => {
    const name = values.name?.trim();
    const description = values.description?.trim();
    const category_id = values.category_id;
    const brand_id = values.brand_id;

    if (!name || !description || !category_id || !brand_id) {
      return Promise.resolve(draftIdRef.current);
    }

    const payload = {
      name,
      name_en: values.name_en?.trim() || null,
      description,
      description_en: values.description_en?.trim() || null,
      category_id,
      brand_id,
    };

    return runSaveQueued(async () => {
      setDraftStatus("saving");
      try {
        const res = await apiFetch(
          draftIdRef.current ? `/product/${draftIdRef.current}` : "/product",
          {
            method: draftIdRef.current ? "PATCH" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error(t("product.couldNotSaveProduct"));
        const product = await res.json();
        draftIdRef.current = product.id;
        setDraftStatus("saved");
        return product.id as string;
      } catch (err) {
        setDraftStatus("error");
        throw err;
      }
    });
  };

  useEffect(() => {
    const subscription = watch((values, { type }) => {
      if (type !== "change") return;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(() => {
        persistDraft(values).catch(() => {});
      }, 800);
    });
    return () => {
      subscription.unsubscribe();
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [watch]);

  // Separate from the autosave watcher above: that one is filtered to
  // real typing ("change") so setValue-driven writes (the category/brand
  // auto-match effects below) don't trigger redundant PATCHes. Readiness
  // has no such concern, and must react to those setValue writes too, so
  // it gets its own unfiltered subscription plus an initial mount check.
  useEffect(() => {
    onFieldsReadyChange?.(REQUIRED_FIELDS_READY(getValues()));
    const subscription = watch(values => {
      onFieldsReadyChange?.(REQUIRED_FIELDS_READY(values));
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    onExposeSubmit?.(() => handleSubmit(submit)());
  });

  useEffect(() => {
    apiFetch("/categories?limit=100")
      .then(res => res.json())
      .then((page: Page<Category>) => setCategories(page.items))
      .catch(() => {});

    apiFetch("/brands?limit=100")
      .then(res => res.json())
      .then((page: Page<Brand>) => setBrands(page.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!defaultValues?.category_id && defaultValues?.category && categories.length) {
      const match = categories.find(c => c.name === defaultValues.category!.name);
      if (match) setValue("category_id", match.id);
    }
  }, [categories, defaultValues, setValue]);

  useEffect(() => {
    if (!defaultValues?.brand_id && defaultValues?.brand && brands.length) {
      const match = brands.find(b => b.name === defaultValues.brand!.name);
      if (match) setValue("brand_id", match.id);
    }
  }, [brands, defaultValues, setValue]);

  const addVariantDraft = () => {
    setVariantDrafts(previous => [...previous, { ...emptyVariant }]);
    setDraftAnnouncementIds(previous => [...previous, null]);
    setDraftUnavailable(previous => [...previous, false]);
  };

  const removeVariantDraft = (index: number) => {
    setVariantDrafts(prev => prev.filter((_, i) => i !== index));
    setDraftAnnouncementIds(previous => previous.filter((_, i) => i !== index));
    setDraftUnavailable(previous => previous.filter((_, i) => i !== index));
  };

  const updateVariantDraft = (index: number, draft: VariantDraft) => {
    setVariantDrafts(prev => prev.map((v, i) => (i === index ? draft : v)));
    setVariantActionNudge(value => value + 1);
  };

  const submit = async (data: ProductFormValues) => {
    if (mode !== "create") {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      try {
        await onSubmit(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t("product.somethingWentWrong");
        setError("root", { message });
      }
      return;
    }

    if (variantDrafts.length === 0) {
      setVariantsError(t("product.atLeastOneVariantRequired"));
      return;
    }
    for (const draft of variantDrafts) {
      const validationError = validateVariantDraft(draft);
      if (validationError) {
        setVariantsError(t(`variant.${validationError}`));
        return;
      }
    }
    setVariantsError(null);

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    try {
      const productId = await persistDraft(data);
      if (!productId) throw new Error(t("product.couldNotSaveProduct"));
      const variants: VariantSubmission[] = variantDrafts.map((draft, index) => ({
        payload: variantDraftToPayload(draft),
        imageFile: draft.imageFile,
        announcement_id: draftAnnouncementIds[index] ?? null,
        is_unavailable: draftUnavailable[index] ?? false,
      }));
      await onSubmit({ ...data, variants, productId });
    } catch (err) {
      const message = err instanceof Error ? err.message : t("product.somethingWentWrong");
      setError("root", { message });
    }
  };

  const isSpanish = activeLanguage === "es";

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4 w-full max-w-md bg-app-surface text-foreground border border-app-border rounded-2xl shadow-md p-6">
      <LanguageTabs active={activeLanguage} onChange={setActiveLanguage} className="self-end" />

      {isSpanish ? (
        <>
          <div className="flex flex-col gap-1">
            <label htmlFor="name">{t("product.name")}</label>
            <input id="name" className="input w-full bg-app-control border-app-border text-foreground" {...register("name", { required: t("common.required") })} />
            {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="description">{t("product.description")}</label>
            <textarea id="description" className="textarea w-full bg-app-control border-app-border text-foreground" {...register("description", { required: t("common.required") })} />
            {errors.description && <span className="text-red-500 text-sm">{errors.description.message}</span>}
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <label htmlFor="name_en">{t("product.nameEn")}</label>
            <input id="name_en" className="input w-full bg-app-control border-app-border text-foreground" {...register("name_en")} />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="description_en">{t("product.descriptionEn")}</label>
            <textarea id="description_en" className="textarea w-full bg-app-control border-app-border text-foreground" {...register("description_en")} />
          </div>
        </>
      )}

      <div className="flex flex-col gap-1">
        <label htmlFor="category_id">{t("product.category")}</label>
        <select
          id="category_id"
          className="input w-full bg-app-control border-app-border text-foreground"
          {...register("category_id", { required: t("common.required"), valueAsNumber: true })}
        >
          <option value="">{t("product.selectCategory")}</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        {errors.category_id && <span className="text-red-500 text-sm">{errors.category_id.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="brand_id">{t("product.brand")}</label>
        <select
          id="brand_id"
          className="input w-full bg-app-control border-app-border text-foreground"
          {...register("brand_id", { required: t("common.required"), valueAsNumber: true })}
        >
          <option value="">{t("product.selectBrand")}</option>
          {brands.map(brand => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>
        {errors.brand_id && <span className="text-red-500 text-sm">{errors.brand_id.message}</span>}
      </div>

      {draftStatus !== "idle" && (
        <span className="text-xs text-app-muted">
          {draftStatus === "saving" && (mode === "create" ? t("product.savingDraft") : t("common.saving"))}
          {draftStatus === "saved" && (mode === "create" ? t("product.draftSaved") : t("product.saved"))}
          {draftStatus === "error" && t("product.couldNotSave")}
        </span>
      )}

      {mode === "create" && (
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-bold text-brand">{t("product.variants")}</h2>
          {variantDrafts.map((draft, index) => (
            <div key={index} className="flex flex-col gap-2 border border-app-border rounded-lg p-3">
              <VariantFields value={draft} onChange={value => updateVariantDraft(index, value)} />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-foreground">
                  {t("announcementAssignment.variantLabel")}
                </label>
                <AnnouncementPicker
                  value={draftAnnouncementIds[index] ?? null}
                  onChange={announcementId => {
                    setDraftAnnouncementIds(previous =>
                      previous.map((current, i) => (i === index ? announcementId : current))
                    );
                    setVariantActionNudge(value => value + 1);
                  }}
                />
              </div>
              <UnavailableToggle
                checked={draftUnavailable[index] ?? false}
                onChange={unavailable => {
                  setDraftUnavailable(previous =>
                    previous.map((current, i) => (i === index ? unavailable : current))
                  );
                  setVariantActionNudge(value => value + 1);
                }}
              />
              {variantDrafts.length > 1 && (
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors duration-200 cursor-pointer"
                  onClick={() => removeVariantDraft(index)}
                >
                  {t("product.remove")}
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-brand border border-brand hover:bg-brand/10 transition-colors duration-200 cursor-pointer"
            onClick={addVariantDraft}
          >
            {t("product.addAnotherVariant")}
          </button>
          {variantsError && <span className="text-red-500 text-sm">{variantsError}</span>}
        </div>
      )}

      {errors.root && <span className="text-red-500 text-sm">{errors.root.message}</span>}

      {!hideSubmitButton && (
        <button
          key={`submit-${variantActionNudge}`}
          type="submit"
          className={`w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
            variantActionNudge ? "variant-action-nudge" : ""
          }`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (pendingLabel ?? t("common.saving")) : submitLabel}
        </button>
      )}
    </form>
  );
}

// Mirrors the create/edit product page shape (title + form card + submit
// button) so it can stand in while auth/product data is still loading,
// instead of a bare loading label.
export function ProductFormSkeleton() {
  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center gap-6 py-8">
      <div className="h-8 w-48 rounded bg-app-surface-hover animate-pulse" />
      <div className="flex flex-col gap-4 w-full bg-app-surface border border-app-border rounded-2xl shadow-md p-6">
        <div className="h-8 w-16 rounded bg-app-surface-hover animate-pulse self-end" />
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="h-4 w-20 rounded bg-app-surface-hover animate-pulse" />
            <div className="h-10 w-full rounded bg-app-surface-hover animate-pulse" />
          </div>
        ))}
      </div>
      <div className="h-12 w-full rounded-2xl bg-app-surface-hover animate-pulse" />
    </div>
  );
}
